/**
 * Subdomain Resolver - Detect and extract subdomain from hostname
 * Used for multi-tenant routing and portal access
 */

/**
 * Extract subdomain from current hostname
 * Returns null for:
 * - localhost
 * - 127.0.0.1
 * - bare domain (empworkspace.com)
 * - be.empworkspace.com (backend API domain)
 * - www.empworkspace.com
 * 
 * @returns {string|null} The subdomain slug, or null if not a valid tenant subdomain
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;

  // Development environments - no subdomain routing.
  // Local override: pass ?slug=<your-org> in the URL to force a specific
  // tenant context. Useful for multi-tenant testing on localhost where
  // every dev would otherwise share one machine.
  if (hostname.includes('localhost') || hostname === '127.0.0.1') {
    try {
      const params = new URLSearchParams(window.location.search);
      const querySlug = params.get('slug');
      if (querySlug && /^[a-z0-9-]+$/.test(querySlug)) return querySlug;
    } catch {
      // ignore
    }
    return null;
  }

  // Extract subdomain parts
  const parts = hostname.split('.');
  
  // Not enough parts for a subdomain (e.g., just 'empworkspace.com')
  if (parts.length < 3) {
    return null;
  }

  const subdomain = parts[0];

  // Exclude reserved subdomains
  const reservedSubdomains = [
    'www',
    'be',
    'api',
    'admin',
    'mail',
    'ftp',
    'smtp',
    'ops', // Super-Admin ops console — never treated as a tenant slug
  ];

  if (reservedSubdomains.includes(subdomain)) {
    return null;
  }

  return subdomain;
};

/**
 * Check if current page is accessed via a tenant subdomain
 * @returns {boolean} True if accessed via a tenant subdomain
 */
export const isAccessedViaSubdomain = () => {
  return getSubdomain() !== null;
};

/**
 * Redirect to portal home if accessed via subdomain
 * This ensures tenant portal is shown instead of landing pages
 * 
 * @param {Function} navigate - React Router navigate function
 */
export const redirectToPortalIfSubdomain = (navigate) => {
  if (isAccessedViaSubdomain()) {
    navigate('/home', { replace: true });
  }
};
